import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { PracticeContactViewModel } from 'chaika-types';
import { infrastructureDivisionId } from 'chaika-enums';

import { store } from '@store';
import { organizationService } from '@organization/service/organizaion/organizationService';
import { userPracticeQueryOptions } from '@user/queries/userPracticeQueries';
import { hasTelegramLink } from '@user/utils/getPracticeTelegramLink';
import { assertIsDefined } from '@utils/assertIsDefined';

interface Props {
    userId: string;
}

interface Data {
    practices: PracticeContactViewModel[];
    practiceWithTgLint: PracticeContactViewModel[];
    hasInfrastructurePractice: boolean;
    isUserBelongToPractice(practiceId: string): boolean;
    getOrganizationPractice(organizationId: string): PracticeContactViewModel[];
}

export function useUserPractice({ userId }: Props): Data {
    const { data: practices = [] } = useQuery({
        ...userPracticeQueryOptions.byUserId(userId),
        enabled: !!userId,
    });

    useEffect(() => {
        organizationService.loadClinics();
    }, []);

    function getOrganizationPractice(organizationId: string): PracticeContactViewModel[] {
        const organization = store.organizationList.get(organizationId);
        return practices.filter(practice => {
            assertIsDefined(organization, `Clinic ${organizationId} is loaded before its practices are shown`);
            return practice.clinicText === organization.abbreviation;
        });
    }

    function isUserBelongToPractice(practiceId: string): boolean {
        return practices.some(practice => practice.id === practiceId);
    }

    return {
        getOrganizationPractice,
        isUserBelongToPractice,
        practices,
        practiceWithTgLint: practices.filter(hasTelegramLink),
        hasInfrastructurePractice: practices.some(({ divisionId }) => divisionId === infrastructureDivisionId),
    };
}
